import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BLOG_POSTS } from '@/mocks/blog';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const BLOG_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog Discovering Marche — Borghi, stile di vita e territorio',
    description: 'Articoli sui borghi storici delle Marche, guida al territorio, stile di vita e cultura marchigiana.',
    url: `${SITE_URL}/blog`,
    publisher: {
      '@type': 'Organization',
      name: 'Discovering Marche',
      logo: 'https://public.readdy.ai/ai/img_res/49b48721-69a4-4c7d-8e06-4224313d172b.png',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
    ],
  },
];

const CATEGORIE = ['Tutti', 'Borghi', 'Stile di vita', 'Guida acquisto', 'Investimenti'];

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const [categoria, setCategoria] = useState('Tutti');
  const [page, setPage] = useState(1);
  const header = useScrollAnimation();
  const grid = useScrollAnimation({ threshold: 0.05 });
  const lang = i18n.language as 'it' | 'en' | 'de';

  const getTitle = (post: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return post.titleEn;
    if (lang === 'de') return post.titleDe;
    return post.title;
  };

  const getExcerpt = (post: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return post.excerptEn;
    if (lang === 'de') return post.excerptDe;
    return post.excerpt;
  };

  const getCategoria = (post: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return post.categoriaEn;
    if (lang === 'de') return post.categoriaDe;
    return post.categoria;
  };

  const getData = (post: typeof BLOG_POSTS[0]) => {
    if (lang === 'en') return post.dataEn;
    if (lang === 'de') return post.dataDe;
    return post.data;
  };

  const filtered = categoria === 'Tutti'
    ? BLOG_POSTS
    : BLOG_POSTS.filter((p) => p.categoria === categoria);

  const PER_PAGE = 6;
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const categoriaColors: Record<string, string> = {
    'Borghi': 'bg-olive-50 text-olive-700 border-olive-200',
    'Stile di vita': 'bg-terracotta-50 text-terracotta-400 border-terracotta-200',
    'Guida acquisto': 'bg-amber-50 text-amber-700 border-amber-200',
    'Investimenti': 'bg-anthracite-50 text-anthracite-600 border-anthracite-200',
  };

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title="Blog Discovering Marche — Borghi, stile di vita e territorio marchigiano"
        description="Articoli e guide sui borghi storici delle Marche, stile di vita marchigiano, cultura e territorio. Scopri le Marche con Discovering Marche. Aggiornato 2026."
        keywords="blog borghi Marche, stile di vita Marche, borghi storici Marche, cultura marchigiana, territorio Marche"
        canonical="/blog"
        jsonLd={BLOG_JSONLD}
      />
      {/* Hero */}
      <section className="relative h-[45vh] min-h-[340px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Marche%20Italy%20lifestyle%20writing%20editorial%20beautiful%20Italian%20countryside%20borghi%20culture%20food%20wine%20landscape%20warm%20light&width=1920&height=700&seq=blogHero&orientation=landscape"
            alt="Blog Marche Casa — borghi storici Marche lifestyle"
            className="w-full h-full object-cover object-center"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-200 mb-4">{t('blog.overline')}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-light max-w-2xl leading-tight">
            {t('blog.title')}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        {/* Category filters */}
        <div
          ref={header.ref}
          className={`fade-in-up ${header.isVisible ? 'visible' : ''} flex flex-wrap gap-3 mb-12`}
        >
          {CATEGORIE.map((cat) => (
            <button
              key={cat}
              onClick={() => { setCategoria(cat); setPage(1); }}
              className={`px-5 py-2 rounded-full font-sans text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                categoria === cat
                  ? 'bg-terracotta-300 border-terracotta-300 text-white'
                  : 'border-anthracite-200 text-anthracite-500 hover:border-terracotta-300 hover:text-terracotta-300'
              }`}
            >
              {cat === 'Tutti' ? t('blog.filter.tutti') : cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div
          ref={grid.ref}
          className={`fade-in-up ${grid.isVisible ? 'visible' : ''} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12`}
        >
          {paginated.map((post, idx) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className={`group block cursor-pointer ${idx === 1 ? 'stagger-2' : idx === 2 ? 'stagger-3' : ''}`}
            >
              <article>
                <div className="relative overflow-hidden rounded-xl aspect-[16/10] mb-5">
                  <img
                    src={post.image}
                    alt={getTitle(post)}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`inline-block font-sans text-xs tracking-widest uppercase px-3 py-1.5 rounded-full border ${
                      categoriaColors[post.categoria] ?? 'bg-white/80 text-anthracite-600 border-white/50'
                    }`}>
                      {getCategoria(post)}
                    </span>
                  </div>
                </div>
                <div className="px-1">
                  <p className="font-sans text-xs text-anthracite-400 mb-3">{getData(post)}</p>
                  <h2 className="font-serif text-xl text-anthracite-800 font-medium mb-3 leading-snug group-hover:text-terracotta-300 transition-colors">
                    {getTitle(post)}
                  </h2>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4 line-clamp-3">
                    {getExcerpt(post)}
                  </p>
                  <span className="inline-flex items-center gap-2 font-sans text-sm text-terracotta-300 group-hover:gap-3 transition-all duration-300 whitespace-nowrap">
                    {t('blog.leggi')}
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-10 h-10 flex items-center justify-center rounded-full font-sans text-sm transition-all cursor-pointer ${
                  p === page
                    ? 'bg-terracotta-300 text-white'
                    : 'border border-anthracite-200 text-anthracite-500 hover:border-terracotta-300 hover:text-terracotta-300'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default BlogPage;
